import GoldMedalIcon from "assets/icons/GoldMedal";
import DonutChart from "Components/charts/DonutChart";
import InfoPopover from "Components/Common/InfoPopover";
import SegmentedSwitcher, { SegmentedOption } from "Components/Common/SegmentedSwitcher";
import React, { useEffect, useMemo, useState } from "react";
import { CompetitionGroup } from "types/competition-analysis";
import { formatNumber, getOrdinal, stringToColor, textToTitleCase } from "utils/helpers";

type Mode =
    | "posts_vs_creators"
    | "posts_vs_engagements"
    | "posts_vs_views"
    | "posts_vs_engagement_score";

const options = [
    { key: "posts_vs_creators", label: "Posts vs Creators" },
    { key: "posts_vs_engagements", label: "Posts vs Engagements" },
    { key: "posts_vs_views", label: "Posts vs Views" },
    { key: "posts_vs_engagement_score", label: "Posts vs Engagement Score" }
] satisfies SegmentedOption<Mode>[];

const MODE_CONFIG: Record<
    Mode,
    {
        leftKey: "posts";
        rightKey: "creators" | "engagements" | "views" | "engagement_score";
        rightLabel: string;
        impactKey:
        | "creators_impact"
        | "engagements_impact"
        | "views_impact"
        | "engagement_score_impact";
    }
> = {
    posts_vs_creators: {
        leftKey: "posts",
        rightKey: "creators",
        rightLabel: "Creators",
        impactKey: "creators_impact"
    },
    posts_vs_engagements: {
        leftKey: "posts",
        rightKey: "engagements",
        rightLabel: "Engagements",
        impactKey: "engagements_impact"
    },
    posts_vs_views: {
        leftKey: "posts",
        rightKey: "views",
        rightLabel: "Views",
        impactKey: "views_impact"
    },
    posts_vs_engagement_score: {
        leftKey: "posts",
        rightKey: "engagement_score",
        rightLabel: "Engagement Score",
        impactKey: "engagement_score_impact"
    }
};
export type ImpactMetric =
    | "creators"
    | "engagements"
    | "views"
    | "engagement_score";


export const IMPACT_INFO: Record<
    ImpactMetric,
    {
        description: string;
        formula: string;
    }
> = {
    creators: {
        description:
            "Impact shows how effective your content is by comparing the percentage of creators involved to the percentage of total posts. It helps you understand whether your content is reaching a wider audience, not just increasing the number of posts.",
        formula:
            "Impact = % of Total Creators ÷ % of Total Posts",
    },

    engagements: {
        description:
            "Impact shows how well your content performs by comparing the percentage of total engagements to the percentage of total posts. It helps you understand whether your posts are generating meaningful audience interactions, not just being published more often.",
        formula:
            "Impact = % of Total Engagements ÷ % of Total Posts",
    },

    views: {
        description:
            "Impact measures how far your content reaches by comparing the percentage of total views to the percentage of total posts. It highlights whether your content is gaining visibility with the audience beyond simply posting more.",
        formula:
            "Impact = % of Total Views ÷ % of Total Posts",
    },

    engagement_score: {
        description:
            "Impact reflects overall content quality by comparing the percentage of engagement score to the percentage of total posts. It helps you understand whether your content is truly resonating with the audience, not just increasing in quantity.",
        formula:
            "Impact = % of Engagement Score ÷ % of Total Posts",
    },
};


const ShareOfVoice: React.FC<{ competitionGroup: CompetitionGroup }> = ({ competitionGroup }) => {
    const [visibleMap, setVisibleMap] = useState<Record<string, boolean>>({});
    const [mode, setMode] = useState<Mode>("posts_vs_creators");
    const [hoveredId, setHoveredId] = useState<string | null>(null);

    // Initialize visibility map
    useEffect(() => {
        const map: Record<string, boolean> = {};
        competitionGroup.accounts.forEach(acc => {
            map[acc.social_account.uuid] = true;
        });
        setVisibleMap(map);
    }, [competitionGroup]);

    const toggleBrand = (id: string) => {
        setVisibleMap(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };


    const rowsRaw = useMemo(() => {
        return competitionGroup.accounts.map(acc => {
            const id = acc.social_account.uuid;
            const color = stringToColor(acc.social_account.handle);

            return {
                id,
                name: acc.social_account.name,
                color,

                posts: acc.total_insight?.posts || 0,
                engagements: acc.total_insight?.engagements || 0,
                creators: acc.total_insight?.creators || 0,
                views: acc.total_insight?.views || 0,
                engagement_score: acc.total_insight?.engagement_score || 0,

                creators_impact: acc.share_of_voice?.creators_impact || 0,
                engagements_impact: acc.share_of_voice?.engagements_impact || 0,
                views_impact: acc.share_of_voice?.views_impact || 0,
                engagement_score_impact: acc.share_of_voice?.engagement_score_impact || 0,

                handle: `@${acc.social_account.handle}`,
                avatar: acc.social_account.profile_picture_url
            };
        });
    }, [competitionGroup]);

    const { leftKey, rightKey, rightLabel, impactKey } = MODE_CONFIG[mode];

    const totalRightMetric = useMemo(() => {
        return rowsRaw.reduce(
            (sum, r) => sum + (r[rightKey] as number),
            0
        );
    }, [rowsRaw, rightKey]);


    // Sort rows by selected right metric (for ranking)
    const rows = useMemo(() => {
        const unsorted = [...rowsRaw];
        const sorted = [...rowsRaw].sort(
            (a, b) => (b[rightKey] as number) - (a[rightKey] as number)
        );

        return unsorted.map((r) => {
            const value = r[rightKey] as number;
            const percentage =
                totalRightMetric > 0
                    ? (value / totalRightMetric) * 100
                    : 0;

            return {
                ...r,
                rank: sorted.findIndex((a) => a.id === r.id) + 1,
                percentage,
                isVisible: visibleMap[r.id] !== false
            }
        });

    }, [rowsRaw, rightKey, visibleMap, totalRightMetric]);

    const leftDonut = useMemo(() => {
        return rows
            .filter(r => r.isVisible)
            .map(r => ({
                id: r.id,
                name: r.name,
                y: r[leftKey],
                color: r.color,
                handle: r.handle,
                avatar: r.avatar
            }));
    }, [rows, leftKey]);

    const rightDonut = useMemo(() => {
        return rows
            .filter(r => r.isVisible)
            .map(r => ({
                id: r.id,
                name: r.name,
                y: r[rightKey],
                color: r.color,
                handle: r.handle,
                avatar: r.avatar
            }));
    }, [rows, rightKey]);

    const renderDonutTooltip = ({
        name,
        value,
        color,
        handle,
        avatar,
        metricLabel
    }: {
        name: string;
        value: number;
        color: string;
        handle?: string;
        avatar?: string;
        metricLabel: string;
    }) => {
        return (
            <div
                style={{
                    display: "flex",
                    background: "#ffffff",
                    borderRadius: 12,
                    padding: "12px 14px",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
                    minWidth: 220,
                    gap: 10,
                    zIndex: 9999,
                    alignItems: "center"
                }}
            >
                <div
                    style={{
                        width: 4,
                        alignSelf: "stretch",
                        borderRadius: 4,
                        background: color
                    }}
                />

                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: "#111827" }}>{name}</div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>{handle}</div>
                    <div style={{ marginTop: 6, fontSize: 14, fontWeight: 600 }}>
                        {formatNumber(value)} {textToTitleCase(metricLabel)}
                    </div>
                </div>

                {avatar && (
                    <img
                        src={avatar}
                        style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            objectFit: "cover"
                        }}
                    />
                )}
            </div>
        );
    };

    return (
        <div className="card shadow m-3 mt-5">
            <div className="card-header bg-danger-light p-4 d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0 heading dashboard-card-heading d-flex align-items-center gap-2">
                    <span className="dashboard-card-icon">
                        <i className="bx bxs-pie-chart-alt-2 mt-1"></i>
                    </span>
                    Share of Voice
                </h5>

                <SegmentedSwitcher
                    options={options}
                    value={mode}
                    onChange={setMode}
                />
            </div>

            <div className="row p-4">
                {/* TABLE */}
                <div className="col-md-7">
                    <table className="table table-bordered table-hover align-middle">
                        <thead>
                            <tr className="text-muted">
                                <th>Competitors</th>
                                <th>No. of Posts</th>
                                <th>{rightLabel}</th>
                                <th className="text-center">
                                    <div className="d-flex justify-content-center align-items-center gap-2">
                                        <span>
                                            Impact
                                        </span>
                                        <span className="mt-2">
                                            <InfoPopover
                                                heading='Impact'
                                                content={
                                                    <div className="d-flex flex-column gap-2">
                                                        <p>{IMPACT_INFO[MODE_CONFIG[mode].rightKey as ImpactMetric].description}</p>
                                                        <strong>Formula:</strong> {IMPACT_INFO[MODE_CONFIG[mode].rightKey as ImpactMetric].formula}
                                                    </div>
                                                }
                                                size={16}
                                            />
                                        </span>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr
                                    key={r.id}
                                    onClick={() => toggleBrand(r.id)}
                                    onMouseEnter={() => setHoveredId(r.id)}
                                    onMouseLeave={() => setHoveredId(null)}
                                    style={{
                                        cursor: "pointer",
                                        opacity: r.isVisible ? 1 : 0.4,
                                        background:
                                            hoveredId === r.id ? "rgba(0,0,0,0.03)" : undefined
                                    }}
                                >
                                    <td>
                                        <span
                                            style={{
                                                display: "inline-block",
                                                width: 8,
                                                height: 8,
                                                borderRadius: "50%",
                                                background: r.isVisible ? r.color : "#d1d5db",
                                                marginRight: 8
                                            }}
                                        ></span>
                                        <strong>{r.name}</strong>
                                    </td>
                                    <td>{formatNumber(r.posts)}</td>
                                    <td>
                                        <div className="sov-cell">
                                            <span className="sov-value">
                                                {formatNumber(r[rightKey])}
                                            </span>

                                            {hoveredId === r.id && (
                                                <span className="sov-percentage text-muted">
                                                    {r.percentage.toFixed(2)}%
                                                </span>
                                            )}
                                        </div>

                                    </td>
                                    <td>
                                        <div className="impact-cell">
                                            <span className="impact-value">
                                                {(r[impactKey] as number).toFixed(2)}x
                                            </span>
                                            {r.rank === 1 ? (
                                                <span className="impact-badge impact-badge--static">
                                                    <GoldMedalIcon size={24} />
                                                </span>
                                            ) : hoveredId === r.id ? (
                                                <span className="impact-badge impact-badge--hover text-muted">
                                                    {r.rank}{getOrdinal(r.rank)}
                                                </span>
                                            ) : null}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* DONUTS */}
                <div className="col-md-5 d-flex justify-content-around p-0">
                    <div style={{ width: 220 }}>
                        <DonutChart
                            title="POSTS"
                            data={leftDonut}
                            hoveredId={hoveredId}
                            tooltipRenderer={renderDonutTooltip}
                        />
                    </div>

                    <div style={{ width: 220 }}>
                        <DonutChart
                            title={rightLabel.toUpperCase()}
                            data={rightDonut}
                            hoveredId={hoveredId}
                            tooltipRenderer={renderDonutTooltip}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShareOfVoice;
